import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import { Box, Modal, Button, Stack, Autocomplete, TextField, Checkbox, Typography, FormControlLabel } from '@mui/material';

import { getTipoAnexo, getUnoAnexo } from './api';
import { cores, labelCampoEscuro } from '../styles/styleUtils';


const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50vw',
  height: '90vh',
  '@media screen and (max-width: 600px)': {
    width: '50vw',
    height: '70vh',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
  alignItems: 'center',
  justifyContent: 'center',
};

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

const sxFontBotaoMobile = {
  '@media screen and (max-width: 600px)': {
    fontSize: 10,
  },
}

// eslint-disable-next-line no-unused-vars
const ModalAddAnexo = React.forwardRef((props, ref) => {
  const [anexo, setAnexo] = useState(props.anexo || null);
  const [tipos, setTipos] = useState([])
  const [notaFiscal, setNotaFiscal] = useState(false)
  
  useEffect(() => {
    fetchTipos();
  }, []);

  const fetchTipos = async () => {
    const result = await getTipoAnexo();

    let tiposFiltrados = result.filter(tipo =>
      !tipo.tipocontratante || tipo.tipocontratante === props.tipocontratante
    );

    // No contexto de montagem: mostrar apenas NF de Montagem e Foto de Montagem
    if (props.montagem) {
      tiposFiltrados = tiposFiltrados.filter(tipo =>
        tipo.nome === 'Nota Fiscal de Montagem' || tipo.nome === 'Foto de Montagem'
      );
    }

    // Para bombeamento: mostrar apenas os tipos permitidos
    if (props.isBombeamento && !props.montagem) {
      const tiposBombeamento = [
        'Localização',
        'Documento Contratante',
        'Fachada da Casa',
        'Outros',
        'Diagrama Unifilar',
        'Nota Fiscal de Montagem',
        'Nota Fiscal de Projeto',
        'Foto de Montagem',
      ];
      tiposFiltrados = tiposFiltrados.filter(tipo => tiposBombeamento.includes(tipo.nome));
    }

    // Para off-grid: mesma lista do bombeamento
    if (props.isOffGrid && !props.montagem) {
      const tiposOffGrid = [
        'Localização',
        'Documento Contratante',
        'Fachada da Casa',
        'Outros',
        'Diagrama Unifilar',
        'Nota Fiscal de Montagem',
        'Nota Fiscal de Projeto',
        'Foto de Montagem',
      ];
      tiposFiltrados = tiposFiltrados.filter(tipo => tiposOffGrid.includes(tipo.nome));
    }

    // Para serviços: mostra apenas os tipos específicos de serviço (nenhum é obrigatório)
    if (props.isServico && !props.montagem) {
      const tiposServico = [
        'Documento Contratante',
        'Matrícula Imóvel',
        'Escritura Pública',
        'CAR',
        'ITR',
        'Outorga',
        'Contrato de compra e venda',
        'Contrato de arrendamento',
        'Projeto Arquitetônico',
        'Projeto Mecânico',
        'Memorial Descritivo',
        'Especificações',
        'Contrato Assinado',
        'Procuração',
        'Nota Fiscal de Serviço',
        'Outros',
      ];
      tiposFiltrados = tiposFiltrados.filter(tipo => tiposServico.includes(tipo.nome));
    }

    // Corrigir exibição
    tiposFiltrados = tiposFiltrados.map(tipo => {
      if (tipo.nome === 'Outros') return { ...tipo, nomeExibicao: 'Outros' };
      return { ...tipo, nomeExibicao: tipo.nome };
    }).filter(tipo => tipo.nome !== 'Fachada da Casa' || tipo.tipoproposta !== null);

    // Remove duplicatas por nome — para off-grid prioriza 'offgrid', para bombeamento prioriza 'bombeamento'
    const nomesVistos = new Map();
    tiposFiltrados.forEach(tipo => {
      if (!nomesVistos.has(tipo.nome)) {
        nomesVistos.set(tipo.nome, tipo);
      } else {
        // Substitui se o atual tiver tipoproposta mais específico
        const atual = nomesVistos.get(tipo.nome);
        if (props.isOffGrid && tipo.tipoproposta === 'offgrid') nomesVistos.set(tipo.nome, tipo);
        else if (props.isBombeamento && tipo.tipoproposta === 'bombeamento') nomesVistos.set(tipo.nome, tipo);
      }
    });
    tiposFiltrados = Array.from(nomesVistos.values());

    setTipos(tiposFiltrados);

    // Pré-seleciona o tipo conforme o botão clicado no ModalFormularioMontagem
    if (props.tipoAnexoMontagem && tiposFiltrados.length > 0) {
      const nomeAlvo = props.tipoAnexoMontagem === 'nf_montagem'
        ? 'Nota Fiscal de Montagem'
        : 'Foto de Montagem';
      const tipoAlvo = tiposFiltrados.find(t => t.nome === nomeAlvo);
      if (tipoAlvo) setAnexo(prev => ({ ...prev, tipo: tipoAlvo }));
    }
  };

  useEffect(() => {
    fetchAnexo();
  }, [props.anexo])
  
  const fetchAnexo = async () => {
    if(anexo && anexo._id){
      const result = await getUnoAnexo(anexo)
      setAnexo({...anexo, base64: result.base64})
    }
  }

  const onConfirm = () => {
    const tipoAnexo = anexo.tipo;
  
    const tipoExistente = props.anexos.some(existingAnexo => {
      const isSameAnexo = 'index' in anexo ? existingAnexo._id === anexo._id : false;
      return (
        existingAnexo.tipo?._id === tipoAnexo._id &&
        tipoAnexo.obrigatorio === true &&
        !isSameAnexo
      );
    });
  
    if (tipoExistente) {
      window.alert("Já existe um anexo obrigatório desse tipo.");
      return;
    }
  
    const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
    const anexoFinal = {
      ...anexo,
      ...(notaFiscal && { notafiscal: true, useranexo: usuarioLogado }),
      ...(props.parecerAcesso && { parecerAcesso: true, useranexo: usuarioLogado }),
      ...(props.montagem && { instalacao: true, useranexo: usuarioLogado }),
    };
    props.onConfirm(anexoFinal);
  };
  
  const addAnexo = () => {
    const fileInputRef = document.createElement('input');
    fileInputRef.type = 'file';
    fileInputRef.style.display = 'none';
    fileInputRef.onchange = (e) => handleImageChange(e);
    document.body.appendChild(fileInputRef);
    fileInputRef.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      const tamanhoMB = (file.size / (1024 * 1024)).toFixed(2).replace('.', ',');
      window.alert(
        `Arquivo muito grande (${tamanhoMB} MB). O tamanho máximo permitido por anexo é de 4 MB.`
      );
      e.target.value = '';
      return;
    }

    const partesNome = file.name.split('.');
    const ext = partesNome.length > 1 ? partesNome.pop() : '';
    const nome = partesNome.join('.') || file.name;

    const reader = new FileReader();
    reader.onloadend = () => {
      const newAnexo = { nome, ext, base64: reader.result };
      setAnexo({ ...anexo, ...newAnexo });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Button color={anexo?.base64 ? 'success' : 'primary'} sx={{...sxFontBotaoMobile, mt: 2, mb: 1}} size={'small'} onClick={addAnexo} variant="contained" >
          {anexo?.base64 ? 'Trocar Arquivo' : 'Escolher Arquivo'}
        </Button>

        <Typography variant="caption" sx={{ color: cores.textoClaro, mb: 2 }}>
          Tamanho máximo por arquivo: 4 MB
        </Typography>

        <TextField 
          sx={{width: 300, backgroundColor: 'white', ...labelCampoEscuro}}
          id="nome" 
          label="Nome" 
          variant="outlined"  
          value={anexo?.nome || ''} 
          onChange={(e) => setAnexo({...anexo, nome: e.target.value})}
        />

        <Autocomplete
          disablePortal
          id="tipo"
          value={anexo?.tipo}
          onChange={(event, newValue) => {
            setAnexo({...anexo, tipo: newValue})
          }}
          options={tipos}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => option.nomeExibicao || option.nome}
          sx={{width: 300, mb: 2, mt:3, backgroundColor: 'white', ...labelCampoEscuro}}
          renderInput={(params) => <TextField {...params} label="Tipo Anexo" />}
        />
        {(props.notafiscal || props.parecerAcesso || props.montagem) && (
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={notaFiscal}
                onChange={() => setNotaFiscal(!notaFiscal)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2" color="warning.main">
                {props.notafiscal ? 'Nota Fiscal' : props.montagem ? 'NF / Foto de Montagem' : 'Parecer de Acesso'} — visível apenas para supervisor e quem anexou
              </Typography>
            }
          />
        )}
        <Stack direction={'row'} justifyContent={'space-between'} gap={20} mt={5} >
          <Button variant="outlined" onClick={props.onClose} color="error"> Cancelar</Button>
          <Button variant="contained" disabled={!anexo?.base64 || !anexo?.nome || !anexo?.tipo} color="success" onClick={onConfirm}>Adicionar</Button>
        </Stack>
      </Box>
    </Modal>
  );
});

ModalAddAnexo.propTypes = {
  open: PropTypes.bool,
  isBombeamento: PropTypes.bool,
  isOffGrid: PropTypes.bool,
  isServico: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default ModalAddAnexo;